import { z } from 'zod/v4-mini';
import { queryProcedure } from '../trpc/common.js';
import { validateOverride } from '../utilities/validateOverride.js';
import type { ApiParams } from '../types/ApiParams.js';
import { errorEntrySource } from '../data/busSources.js';
import { matchingZodObject } from '../utilities/matchingZodObject.js';
import type { OverrideOptions } from '../types/OverrideOptions.js';

export type ValidatePackageOverrideInput = Omit<OverrideOptions, 'version' | 'initiatedBy'>;

const ZodValidatePackageOverrideInput = matchingZodObject<ValidatePackageOverrideInput>({
  packageName: z.string(),
  importPath: z.string(),
  issuerUrl: z.optional(z.string()),
});

export interface ValidatePackageOverrideOutput {
  fixable: boolean;
}

const ZodValidatePackageOverrideOutput = matchingZodObject<ValidatePackageOverrideOutput>({
  fixable: z.boolean(),
});

export async function validatePackageOverride(
  params: ApiParams<ValidatePackageOverrideInput, 'bus' | 'packages', 'config' | 'resolveMap'>,
): Promise<ValidatePackageOverrideOutput> {
  const { input, ctx } = params;
  if (ctx.session.config.features?.autoUpdateEntries) {
    // We need to publish an empty string to the errorEntrySource as the error has been received on the overlay.
    ctx.bus.publish(errorEntrySource, '');
  }

  return {
    fixable: await validateOverride(input, ctx),
  };
}

validatePackageOverride.procedure = queryProcedure<ValidatePackageOverrideInput, ValidatePackageOverrideOutput>(
  validatePackageOverride,
  ZodValidatePackageOverrideInput,
  ZodValidatePackageOverrideOutput,
);
